import React, { useState, useMemo, useEffect } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { Button } from './ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from './ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from './ui/dropdown-menu';
import { 
  Cross, 
  Share2, 
  Link2, 
  Check,
  ImageIcon,
  ArrowRight,
  ArrowLeft,
  BedDouble,
  Armchair,
  Stethoscope,
  LayoutGrid,
  X
} from 'lucide-react';
import { toast } from 'sonner';

// Category icons mapping
const categoryIcons = {
  all: LayoutGrid,
  beds: BedDouble,
  chairs: Armchair,
  equipment: Stethoscope,
};

const ProductsSection = () => {
  const { t, isRTL } = useLanguage();
  const [activeFilter, setActiveFilter] = useState('all');
  const [copiedProductId, setCopiedProductId] = useState(null);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const ArrowIcon = isRTL ? ArrowLeft : ArrowRight;

  // Filter categories
  const filterCategories = [
    { id: 'all', labelKey: 'products.filterAll', icon: categoryIcons.all },
    { id: 'beds', labelKey: 'products.filterBeds', icon: categoryIcons.beds },
    { id: 'chairs', labelKey: 'products.filterChairs', icon: categoryIcons.chairs },
    { id: 'equipment', labelKey: 'products.filterEquipment', icon: categoryIcons.equipment },
  ];

  // Products data - Categories: 'beds', 'chairs', 'equipment'
  const products = [
    // ===== CHAIRS CATEGORY =====
    {
      id: 'wheelchair-standard-kjw610',
      name: { 
        en: 'Wheelchair Standard – Model KJW-610', 
        ar: 'كرسي متحرك قياسي – موديل KJW-610' 
      },
      description: { 
        en: 'High-quality standard wheelchair designed for daily use and patient mobility. Features a durable powder-coated steel frame, comfortable black nylon upholstery with seat belt, swing-away leg rests, and removable armrests for ease of use. Equipped with non-quick release rear wheels and steel hand rims, ensuring stability, safety, and long-lasting performance.',
        ar: 'كرسي متحرك قياسي عالي الجودة مصمم للاستخدام اليومي وتنقل المرضى. يتميز بإطار فولاذي متين مطلي بالبودرة، وتنجيد نايلون أسود مريح مع حزام أمان، ومساند أرجل قابلة للطي، ومساند أذرع قابلة للإزالة لسهولة الاستخدام. مجهز بعجلات خلفية غير سريعة التحرير وحواف يد فولاذية، مما يضمن الثبات والأمان والأداء طويل الأمد.'
      },
      image: '/images/products/wheelchair-standard-kjw610.png',
      category: 'chairs',
    },
    {
      id: 'commode-wheelchair-kjw313a',
      name: { 
        en: 'Commode Wheelchair – 2 Big Wheels (Model KJW-313a)', 
        ar: 'كرسي متحرك مع مرحاض – عجلتين كبيرتين (موديل KJW-313a)' 
      },
      description: { 
        en: 'High-quality commode wheelchair designed for patient comfort and daily care needs. Features a durable steel frame, large rear wheels for improved mobility, integrated commode seat for hygiene support, comfortable armrests, and swing-away leg rests. Ideal for home care, hospitals, and long-term care facilities, providing safety, stability, and ease of use.',
        ar: 'كرسي متحرك مع مرحاض عالي الجودة مصمم لراحة المريض واحتياجات الرعاية اليومية. يتميز بإطار فولاذي متين، وعجلات خلفية كبيرة لتحسين الحركة، ومقعد مرحاض متكامل للدعم الصحي، ومساند أذرع مريحة، ومساند أرجل قابلة للطي. مثالي للرعاية المنزلية والمستشفيات ومرافق الرعاية طويلة الأمد، يوفر الأمان والثبات وسهولة الاستخدام.'
      },
      image: '/images/products/commode-wheelchair-kjw313a.png',
      category: 'chairs',
    },
    {
      id: 'wheelchair-longback-kjw610',
      name: { 
        en: 'Wheelchair Long Back – Model KJW-610', 
        ar: 'كرسي متحرك بظهر طويل – موديل KJW-610' 
      },
      description: { 
        en: 'High-back manual wheelchair designed for enhanced comfort and patient support. Features a durable chrome steel frame, reclining long backrest with padded head support, comfortable upholstery, detachable armrests, and adjustable leg rests. Equipped with large rear wheels for smooth mobility, making it suitable for hospitals, home care, and long-term care facilities.',
        ar: 'كرسي متحرك يدوي بظهر عالٍ مصمم لراحة معززة ودعم المريض. يتميز بإطار فولاذي كروم متين، ومسند ظهر طويل قابل للإمالة مع دعم رأس مبطن، وتنجيد مريح، ومساند أذرع قابلة للفك، ومساند أرجل قابلة للتعديل. مجهز بعجلات خلفية كبيرة للتنقل السلس، مما يجعله مناسبًا للمستشفيات والرعاية المنزلية ومرافق الرعاية طويلة الأمد.'
      },
      image: '/images/products/wheelchair-longback-kjw610.png',
      category: 'chairs',
    },
    {
      id: 'commode-wheelchair-4wheels-kjw313',
      name: { 
        en: 'Commode Wheelchair – 4 Small Wheels (Model KJW-313)', 
        ar: 'كرسي متحرك مع مرحاض – 4 عجلات صغيرة (موديل KJW-313)' 
      },
      description: { 
        en: 'Compact commode wheelchair designed for daily patient care and indoor use. Features a durable steel frame, integrated commode seat for hygiene support, comfortable padded seat and backrest, fixed armrests, and swing-away leg rests. Equipped with four small wheels for easy maneuverability in tight spaces, making it ideal for hospitals, clinics, and home care environments.',
        ar: 'كرسي متحرك مع مرحاض مدمج مصمم للرعاية اليومية للمرضى والاستخدام الداخلي. يتميز بإطار فولاذي متين، ومقعد مرحاض متكامل للدعم الصحي، ومقعد ومسند ظهر مبطنين مريحين، ومساند أذرع ثابتة، ومساند أرجل قابلة للطي. مجهز بأربع عجلات صغيرة لسهولة المناورة في الأماكن الضيقة، مما يجعله مثاليًا للمستشفيات والعيادات وبيئات الرعاية المنزلية.'
      },
      image: '/images/products/commode-wheelchair-4wheels-kjw313.png',
      category: 'chairs',
    },
    {
      id: 'wheelchair-lightweight-kjw610a',
      name: { 
        en: 'Lightweight Wheelchair – Model KJW-610a', 
        ar: 'كرسي متحرك خفيف الوزن – موديل KJW-610a' 
      },
      description: { 
        en: 'Lightweight manual wheelchair designed for easy handling and daily patient mobility. Features a durable lightweight frame with liquid color coating, folding back handles for compact storage, anti-tipper system for added safety, and quick-release rear wheels for convenience. Designed without hand brakes, making it suitable for hospitals, clinics, and home care use.',
        ar: 'كرسي متحرك يدوي خفيف الوزن مصمم لسهولة التعامل والتنقل اليومي للمرضى. يتميز بإطار خفيف الوزن متين بطلاء سائل ملون، ومقابض ظهر قابلة للطي للتخزين المدمج، ونظام مضاد للانقلاب لمزيد من الأمان، وعجلات خلفية سريعة التحرير للراحة. مصمم بدون فرامل يدوية، مما يجعله مناسبًا للمستشفيات والعيادات والرعاية المنزلية.'
      },
      image: '/images/products/wheelchair-lightweight-kjw610a.png',
      category: 'chairs',
    },
    // ===== BEDS CATEGORY =====
    {
      id: 'hospital-bed-manual',
      name: { 
        en: 'Adjustable Hospital Bed – Manual', 
        ar: 'سرير مستشفى قابل للتعديل – يدوي' 
      },
      description: { 
        en: 'High-quality manual hospital bed designed for patient comfort and long-term care. Features an adjustable backrest and leg section, durable steel frame, smooth mobility caster wheels with locking system, and protective side rails for patient safety. Ideal for hospitals, clinics, and home care environments, offering reliability, stability, and ease of use.',
        ar: 'سرير مستشفى يدوي عالي الجودة مصمم لراحة المريض والرعاية طويلة الأمد. يتميز بمسند ظهر وقسم أرجل قابلين للتعديل، وإطار فولاذي متين، وعجلات متحركة ناعمة مع نظام قفل، وقضبان جانبية واقية لسلامة المريض. مثالي للمستشفيات والعيادات وبيئات الرعاية المنزلية، يوفر الموثوقية والثبات وسهولة الاستخدام.'
      },
      image: '/images/products/hospital-bed-manual.jpg',
      category: 'beds',
    },
    {
      id: 'hospital-bed-multifunction',
      name: { 
        en: 'Adjustable Hospital Bed – Multi-Function', 
        ar: 'سرير مستشفى قابل للتعديل – متعدد الوظائف' 
      },
      description: { 
        en: 'Multi-function adjustable hospital bed designed for patient comfort and advanced care needs. Features an adjustable backrest and leg section, sturdy steel frame, foldable side rails for patient safety, and smooth caster wheels with locking system for easy mobility. Suitable for hospitals, clinics, and long-term home care, providing stability, safety, and ease of operation.',
        ar: 'سرير مستشفى متعدد الوظائف قابل للتعديل مصمم لراحة المريض واحتياجات الرعاية المتقدمة. يتميز بمسند ظهر وقسم أرجل قابلين للتعديل، وإطار فولاذي متين، وقضبان جانبية قابلة للطي لسلامة المريض، وعجلات متحركة ناعمة مع نظام قفل لسهولة التنقل. مناسب للمستشفيات والعيادات والرعاية المنزلية طويلة الأمد، يوفر الثبات والأمان وسهولة التشغيل.'
      },
      image: '/images/products/hospital-bed-multifunction.jpg',
      category: 'beds',
    },
    {
      id: 'hospital-bed-adjustable-backrest',
      name: { 
        en: 'Manual Hospital Bed – Adjustable Backrest', 
        ar: 'سرير مستشفى يدوي – مسند ظهر قابل للتعديل' 
      },
      description: { 
        en: 'Manual adjustable hospital bed designed for basic patient care and comfort. Features an adjustable backrest operated manually, durable steel frame, protective side rails for patient safety, and smooth caster wheels with locking system for easy movement and stability. Suitable for hospitals, clinics, and home care environments.',
        ar: 'سرير مستشفى يدوي قابل للتعديل مصمم للرعاية الأساسية للمريض والراحة. يتميز بمسند ظهر قابل للتعديل يدويًا، وإطار فولاذي متين، وقضبان جانبية واقية لسلامة المريض، وعجلات متحركة ناعمة مع نظام قفل لسهولة الحركة والثبات. مناسب للمستشفيات والعيادات وبيئات الرعاية المنزلية.'
      },
      image: '/images/products/hospital-bed-adjustable-backrest.jpg',
      category: 'beds',
    },
    {
      id: 'hospital-bed-full-adjustable',
      name: { 
        en: 'Manual Hospital Bed – Full Adjustable with Side Rails', 
        ar: 'سرير مستشفى يدوي – قابل للتعديل بالكامل مع قضبان جانبية' 
      },
      description: { 
        en: 'Full-length manual hospital bed designed for reliable patient care and comfort. Features adjustable backrest and leg sections operated manually, durable steel frame, full protective side rails for enhanced patient safety, and smooth caster wheels with locking system for easy movement and stability. Ideal for hospitals, clinics, and home care use.',
        ar: 'سرير مستشفى يدوي كامل الطول مصمم للرعاية الموثوقة للمريض والراحة. يتميز بمسند ظهر وأقسام أرجل قابلة للتعديل تعمل يدويًا، وإطار فولاذي متين، وقضبان جانبية واقية كاملة لتعزيز سلامة المريض، وعجلات متحركة ناعمة مع نظام قفل لسهولة الحركة والثبات. مثالي للمستشفيات والعيادات والرعاية المنزلية.'
      },
      image: '/images/products/hospital-bed-full-adjustable.jpg',
      category: 'beds',
    },
    // ===== EQUIPMENT CATEGORY =====
    {
      id: 'medical-walker-with-wheels',
      name: { 
        en: 'Medical Walker with Wheels', 
        ar: 'مشاية طبية بعجل' 
      },
      description: { 
        en: 'Durable and lightweight medical walker designed to provide stability and support for patients during movement. Equipped with front wheels for easy mobility and adjustable height to suit different users. Ideal for elderly and rehabilitation use.',
        ar: 'مشاية طبية قوية وخفيفة الوزن مصممة لتوفير الثبات والدعم أثناء الحركة. مزودة بعجلات أمامية لسهولة التنقل وارتفاع قابل للتعديل ليناسب مختلف المستخدمين. مثالية لكبار السن وفترات التأهيل.'
      },
      image: '/images/products/medical-walker-with-wheels.jpeg',
      category: 'equipment',
    },
    {
      id: 'foldable-medical-commode-chair',
      name: { 
        en: 'Foldable Medical Commode Chair', 
        ar: 'كرسي حمام طبي متحرك' 
      },
      description: { 
        en: 'Practical and sturdy foldable commode chair designed to provide comfort and safety for patients and elderly users. Comes with a removable bucket for easy cleaning, supportive armrests, and adjustable height for maximum convenience. Ideal for home care and rehabilitation.',
        ar: 'كرسي حمام طبي عملي وقوي مصمم لتوفير الراحة والأمان للمرضى وكبار السن. مزود بحوض قابل للإزالة لسهولة التنظيف، مع مساند جانبية للدعم، وارتفاع قابل للتعديل لراحة أكبر. مثالي للاستخدام المنزلي وفترات التأهيل.'
      },
      image: '/images/products/foldable-medical-commode-chair.jpeg',
      category: 'equipment',
    },
    {
      id: 'trister-bp-monitor',
      name: { 
        en: 'Trister Digital Blood Pressure Monitor', 
        ar: 'جهاز قياس ضغط الدم الرقمي تريستر' 
      },
      description: { 
        en: 'Accurate and easy-to-use digital blood pressure monitor designed for home and clinical use. Features a clear digital display, automatic measurement system, and memory function for tracking readings. Comes with a comfortable arm cuff and provides fast, reliable results for daily health monitoring.',
        ar: 'جهاز قياس ضغط الدم الرقمي دقيق وسهل الاستخدام، مناسب للاستخدام المنزلي والعيادات. يتميز بشاشة رقمية واضحة، ونظام قياس أوتوماتيكي، مع خاصية حفظ القراءات لمتابعة الحالة الصحية. مزود بكُف مريح للذراع ويعطي نتائج سريعة وموثوقة.'
      },
      image: '/images/products/trister-bp-monitor.png',
      category: 'equipment',
    },
  ];

  // Check URL for product item parameter and open modal automatically
  useEffect(() => {
    const checkUrlForProduct = () => {
      const hash = window.location.hash;
      if (hash.includes('#products')) {
        const params = new URLSearchParams(hash.split('?')[1] || '');
        const itemId = params.get('item');
        if (itemId) {
          const product = products.find(p => p.id === itemId);
          if (product) {
            // Set the appropriate filter category
            setActiveFilter(product.category);
            // Small delay to ensure DOM is ready, then open modal
            setTimeout(() => {
              setSelectedProduct(product);
            }, 300);
          }
        }
      }
    };

    // Check on initial load
    checkUrlForProduct();

    // Also listen for hash changes
    window.addEventListener('hashchange', checkUrlForProduct);
    return () => window.removeEventListener('hashchange', checkUrlForProduct);
  }, []);

  // Filter products based on active category
  const filteredProducts = useMemo(() => {
    if (activeFilter === 'all') {
      return products;
    }
    return products.filter(product => product.category === activeFilter);
  }, [activeFilter, products]);

  // Get category label for display
  const getCategoryLabel = (categoryId) => {
    const category = filterCategories.find(c => c.id === categoryId);
    return category ? t(category.labelKey) : categoryId;
  };

  // Generate shareable URL that goes through backend for rich social media previews
  const getProductUrl = (productId) => {
    const backendUrl = process.env.REACT_APP_BACKEND_URL || window.location.origin;
    const lang = isRTL ? 'ar' : 'en';
    return `${backendUrl}/api/share/product/${productId}?lang=${lang}`;
  };

  // Direct link to product (for internal use)
  const getDirectProductUrl = (productId) => {
    const baseUrl = window.location.origin;
    return `${baseUrl}/#products?item=${productId}`;
  };

  const getAllProductsUrl = () => {
    const baseUrl = window.location.origin;
    return `${baseUrl}/#products`;
  };

  const copyToClipboard = async (text, productId = null) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedProductId(productId);
      toast.success(t('products.linkCopied'), {
        duration: 2000,
      });
      setTimeout(() => setCopiedProductId(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const shareProduct = async (product) => {
    const url = getProductUrl(product.id);
    const title = isRTL ? product.name.ar : product.name.en;
    const description = isRTL ? product.description.ar : product.description.en;
    const shortDesc = description.length > 150 ? description.substring(0, 150) + '...' : description;
    const categoryLabel = getCategoryLabel(product.category);
    
    // Create rich share text with product details
    const shareText = isRTL 
      ? `🏥 ${title}\n\n📋 الفئة: ${categoryLabel}\n\n📝 ${shortDesc}\n\n🔗 شاهد المنتج:`
      : `🏥 ${title}\n\n📋 Category: ${categoryLabel}\n\n📝 ${shortDesc}\n\n🔗 View Product:`;
    
    if (navigator.share) {
      try {
        await navigator.share({
          title: `${title} | AFAQ ALNABTH Medical Supplies`,
          text: shareText,
          url: url,
        });
      } catch (err) {
        if (err.name !== 'AbortError') {
          // Copy formatted text with URL for richer paste
          const fullShareText = `${shareText}\n${url}`;
          copyToClipboard(fullShareText, product.id);
        }
      }
    } else {
      // Copy formatted text with URL for richer paste
      const fullShareText = `${shareText}\n${url}`;
      copyToClipboard(fullShareText, product.id);
    }
  };

  const shareAllProducts = async () => {
    const url = getAllProductsUrl();
    
    if (navigator.share) {
      try {
        await navigator.share({
          title: t('products.title'),
          text: `${t('products.title')} - AFAQ ALNABTH Medical Supplies`,
          url: url,
        });
      } catch (err) {
        if (err.name !== 'AbortError') {
          copyToClipboard(url, 'all');
        }
      }
    } else {
      copyToClipboard(url, 'all');
    }
  };

  return (
    <section id="products" className="py-24 bg-white relative">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-8">
          <span className="inline-block px-4 py-1.5 bg-blue-50 text-brand-blue text-sm font-semibold rounded-full mb-4">
            {t('products.badge')}
          </span>
          <h2 className="text-3xl sm:text-4xl font-bold text-slate-800 mb-4">
            {t('products.title')}
          </h2>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto mb-8">
            {t('products.description')}
          </p>

          {/* Share All Products Button */}
          <div className="flex justify-center mb-10">
            <Button
              onClick={shareAllProducts}
              className="group relative bg-brand-blue hover:bg-blue-700 text-white px-8 py-6 text-base 
                         shadow-lg shadow-blue-600/25 hover:shadow-xl hover:shadow-blue-600/35
                         transition-all duration-300 ease-out
                         hover:-translate-y-0.5 hover:scale-[1.02]
                         border-2 border-transparent hover:border-brand-red/20"
            >
              <div className="relative flex items-center gap-3">
                <div className="relative">
                  <Cross className="w-5 h-5 transition-transform duration-300 group-hover:rotate-12" />
                  <div className="absolute inset-0 bg-white/20 rounded-full blur-md opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                </div>
                <span>{t('products.shareAll')}</span>
                <Share2 className="w-4 h-4 opacity-70 group-hover:opacity-100 transition-all duration-300 group-hover:translate-x-1" />
              </div>
              <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-0 h-0.5 bg-brand-red group-hover:w-3/4 transition-all duration-300"></div>
            </Button>
          </div>
        </div>

        {/* Category Filters */}
        <div className="mb-10">
          {/* Desktop Filters */}
          <div className="hidden sm:flex justify-center items-center gap-3 flex-wrap">
            {filterCategories.map((category) => {
              const IconComponent = category.icon;
              const isActive = activeFilter === category.id;
              return (
                <button
                  key={category.id}
                  onClick={() => setActiveFilter(category.id)}
                  className={`
                    flex items-center gap-2 px-5 py-3 rounded-full text-sm font-medium
                    transition-all duration-300 ease-out
                    ${isActive 
                      ? 'bg-brand-blue text-white shadow-lg shadow-blue-500/30 scale-105' 
                      : 'bg-slate-100 text-slate-600 hover:bg-blue-50 hover:text-brand-blue hover:shadow-md'
                    }
                  `}
                >
                  <IconComponent className={`w-4 h-4 ${isActive ? 'text-white' : ''}`} />
                  <span>{t(category.labelKey)}</span>
                  {isActive && (
                    <span className="ml-1 px-2 py-0.5 bg-white/20 rounded-full text-xs">
                      {filteredProducts.length}
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          {/* Mobile Filters - Horizontal Scroll */}
          <div className="sm:hidden overflow-x-auto pb-2 -mx-4 px-4">
            <div className="flex gap-2 min-w-max">
              {filterCategories.map((category) => {
                const IconComponent = category.icon;
                const isActive = activeFilter === category.id;
                return (
                  <button
                    key={category.id}
                    onClick={() => setActiveFilter(category.id)}
                    className={`
                      flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-medium
                      transition-all duration-300 whitespace-nowrap
                      ${isActive 
                        ? 'bg-brand-blue text-white shadow-md' 
                        : 'bg-slate-100 text-slate-600 active:bg-blue-50'
                      }
                    `}
                  >
                    <IconComponent className="w-4 h-4" />
                    <span>{t(category.labelKey)}</span>
                    {isActive && (
                      <span className="ml-1 px-1.5 py-0.5 bg-white/20 rounded-full text-xs">
                        {filteredProducts.length}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* Products Grid */}
        {filteredProducts.length > 0 ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
            {filteredProducts.map((product, index) => {
              const productName = isRTL ? product.name.ar : product.name.en;
              const productDesc = isRTL ? product.description.ar : product.description.en;
              const CategoryIcon = categoryIcons[product.category] || Cross;
              
              return (
                <div
                  key={product.id}
                  className="group bg-white rounded-2xl overflow-hidden border border-slate-100 
                             hover:border-blue-200 shadow-sm hover:shadow-xl hover:shadow-blue-100/50 
                             transition-all duration-300 hover:-translate-y-1"
                >
                  {/* Product Image Placeholder */}
                  <div className="relative h-56 bg-gradient-to-br from-slate-100 via-blue-50 to-slate-100 overflow-hidden">
                    {product.image ? (
                      <img
                        src={product.image}
                        alt={productName}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    ) : (
                      <div className="absolute inset-0 flex flex-col items-center justify-center text-slate-400">
                        <div className="w-20 h-20 rounded-full bg-white/80 flex items-center justify-center mb-3 shadow-sm">
                          <ImageIcon className="w-10 h-10 text-slate-300" />
                        </div>
                        <span className="text-sm font-medium">{t('products.comingSoon')}</span>
                      </div>
                    )}
                    
                    {/* Category Badge */}
                    <div className={`absolute top-4 ${isRTL ? 'right-4' : 'left-4'}`}>
                      <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white/95 backdrop-blur-sm 
                                       rounded-full text-xs font-semibold text-brand-blue shadow-sm">
                        <CategoryIcon className="w-3.5 h-3.5 text-brand-red" />
                        {getCategoryLabel(product.category)}
                      </span>
                    </div>

                    {/* Share Button - Individual Product */}
                    <div className={`absolute top-4 ${isRTL ? 'left-4' : 'right-4'}`}>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <button
                            className="group/btn w-10 h-10 rounded-full bg-white/95 backdrop-blur-sm 
                                       flex items-center justify-center shadow-sm
                                       border-2 border-transparent
                                       hover:bg-brand-blue hover:text-white hover:border-brand-red/30
                                       hover:shadow-lg hover:shadow-blue-500/25
                                       hover:scale-110
                                       transition-all duration-300 ease-out"
                            aria-label={t('products.shareProduct')}
                          >
                            {copiedProductId === product.id ? (
                              <Check className="w-4 h-4 text-green-500" />
                            ) : (
                              <Share2 className="w-4 h-4 text-brand-blue group-hover/btn:text-white transition-colors" />
                            )}
                          </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align={isRTL ? 'start' : 'end'} className="w-48">
                          <DropdownMenuItem 
                            onClick={() => shareProduct(product)}
                            className="cursor-pointer hover:bg-blue-50"
                          >
                            <Share2 className={`w-4 h-4 ${isRTL ? 'ml-2' : 'mr-2'} text-brand-blue`} />
                            <span>{t('products.shareProduct')}</span>
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem 
                            onClick={() => copyToClipboard(getProductUrl(product.id), product.id)}
                            className="cursor-pointer hover:bg-blue-50"
                          >
                            <Link2 className={`w-4 h-4 ${isRTL ? 'ml-2' : 'mr-2'} text-brand-blue`} />
                            <span>{t('products.copyLink')}</span>
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </div>

                  {/* Product Content */}
                  <div className="p-6">
                    <h3 className="text-lg font-semibold text-slate-800 mb-2 group-hover:text-brand-blue transition-colors">
                      {productName}
                    </h3>
                    <p className="text-sm text-slate-600 leading-relaxed mb-4 line-clamp-2">
                      {productDesc}
                    </p>
                    
                    {/* View Details Button */}
                    <button 
                      onClick={() => setSelectedProduct(product)}
                      className={`flex items-center text-brand-blue text-sm font-medium 
                                  group-hover:text-brand-red transition-colors duration-300 ${isRTL ? 'flex-row-reverse' : ''}`}
                    >
                      <span>{t('products.viewProducts')}</span>
                      <ArrowIcon className={`w-4 h-4 ${isRTL ? 'mr-1 group-hover:-translate-x-1' : 'ml-1 group-hover:translate-x-1'} transition-transform`} />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          /* No Products Message */
          <div className="text-center py-16">
            <div className="w-20 h-20 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-4">
              <ImageIcon className="w-10 h-10 text-slate-300" />
            </div>
            <p className="text-lg text-slate-500">{t('products.noProducts')}</p>
          </div>
        )}

        {/* Bottom CTA */}
        <div className="text-center mt-12">
          <Button 
            className="bg-slate-800 hover:bg-slate-900 text-white px-8 py-6 text-base 
                       shadow-lg hover:shadow-xl transition-all duration-300"
          >
            {t('products.viewCatalog')}
            <ArrowIcon className={`w-5 h-5 ${isRTL ? 'mr-2' : 'ml-2'}`} />
          </Button>
        </div>
      </div>

      {/* Product Detail Modal */}
      <Dialog open={!!selectedProduct} onOpenChange={(open) => !open && setSelectedProduct(null)}>
        <DialogContent className={`max-w-2xl ${isRTL ? 'rtl' : ''}`}>
          {selectedProduct && (
            <>
              <DialogHeader className={isRTL ? 'text-right' : ''}>
                <DialogTitle className="text-2xl font-bold text-slate-800">
                  {isRTL ? selectedProduct.name.ar : selectedProduct.name.en}
                </DialogTitle>
                <div className="flex items-center gap-2 mt-2">
                  {(() => {
                    const CategoryIcon = categoryIcons[selectedProduct.category] || Cross;
                    return (
                      <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-50 
                                       rounded-full text-xs font-semibold text-brand-blue">
                        <CategoryIcon className="w-3.5 h-3.5 text-brand-red" />
                        {getCategoryLabel(selectedProduct.category)}
                      </span>
                    );
                  })()}
                </div>
              </DialogHeader>
              
              <div className="mt-4">
                {/* Product Image */}
                <div className="relative h-64 bg-gradient-to-br from-slate-100 via-blue-50 to-slate-100 rounded-lg overflow-hidden mb-6">
                  {selectedProduct.image ? (
                    <img
                      src={selectedProduct.image}
                      alt={isRTL ? selectedProduct.name.ar : selectedProduct.name.en}
                      className="w-full h-full object-contain"
                    />
                  ) : (
                    <div className="absolute inset-0 flex flex-col items-center justify-center text-slate-400">
                      <div className="w-20 h-20 rounded-full bg-white/80 flex items-center justify-center mb-3 shadow-sm">
                        <ImageIcon className="w-10 h-10 text-slate-300" />
                      </div>
                      <span className="text-sm font-medium">{t('products.comingSoon')}</span>
                    </div>
                  )}
                </div>

                {/* Product Description */}
                <DialogDescription className={`text-base text-slate-600 leading-relaxed ${isRTL ? 'text-right' : ''}`}>
                  {isRTL ? selectedProduct.description.ar : selectedProduct.description.en}
                </DialogDescription>

                {/* Share Button */}
                <div className={`mt-6 flex ${isRTL ? 'justify-start' : 'justify-end'}`}>
                  <Button
                    onClick={() => shareProduct(selectedProduct)}
                    variant="outline"
                    className="gap-2 border-brand-blue text-brand-blue hover:bg-brand-blue hover:text-white"
                  >
                    <Share2 className="w-4 h-4" />
                    {t('products.shareProduct')}
                  </Button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
};

export default ProductsSection;
